import {Alert, Badge, Card, Group, Stack, Table, Text, Title} from '@mantine/core';
import {AiOutlineLock, AiOutlineUnlock} from 'react-icons/ai';

import {
    ApplicationInfrastructure,
    InfrastructureOrigin,
} from '../types-infrastructure';

// FNP-1016: render an EXISTING inventory record.
//
// ⚠️ This is what stands in place of offering to register again. An application
// that already has a record must not be offered a "register" affordance —
// the entity is append-only per PROVISIONING ACT, so a second row created by a
// confused admin is not a correction, it is a second inventory claiming to
// describe the same resources.

const originLabel = (origin: InfrastructureOrigin) =>
    origin === InfrastructureOrigin.REGISTERED
        ? 'Registered by an administrator'
        : 'Provisioned by the platform';

export const ApplicationInfrastructureProfile = (
    {record}: { record: ApplicationInfrastructure }) => {

    const isRegistered = record.origin === InfrastructureOrigin.REGISTERED;
    const stages = record.stages ?? [];

    return (
        <Stack gap={'lg'}>
            <Group
                gap={'sm'}
                wrap={'wrap'}
            >
                <Title order={4}>Infrastructure</Title>
                <Badge
                    variant={'light'}
                    color={isRegistered ? 'blue' : 'grape'}
                >
                    {record.origin}
                </Badge>
                <Badge variant={'light'}>{record.infrastructureStatus}</Badge>
                {/*
                    Protection is SURFACED, never offered as a toggle. A
                    registered record describes a live production system this
                    platform never built, and its inventory is an admin's
                    assertion rather than an orchestrator's record.
                */}
                <Badge
                    variant={'light'}
                    color={record.protected ? 'green' : 'orange'}
                    leftSection={record.protected ? <AiOutlineLock/> : <AiOutlineUnlock/>}
                >
                    {record.protected ? 'Protected' : 'Not protected'}
                </Badge>
            </Group>

            <Text
                size={'sm'}
                c={'dimmed'}
            >
                {originLabel(record.origin)}.
            </Text>

            {record.protected ? (
                <Alert
                    variant={'light'}
                    color={'green'}
                    icon={<AiOutlineLock/>}
                    title={'Protected from teardown'}
                >
                    <Text size={'sm'}>
                        Decommissioning will refuse to tear down this infrastructure while the
                        record is protected.
                    </Text>
                </Alert>
            ) : null}

            <Card
                withBorder
                padding={'md'}
            >
                <Stack gap={'xs'}>
                    <Title order={5}>Shared resources</Title>
                    <Text size={'sm'}>
                        <Text
                            span
                            fw={600}
                        >
                            Source repository:
                        </Text>{' '}
                        {record.sourceRepositoryName ?? 'not recorded'}
                    </Text>
                    <Text size={'sm'}>
                        <Text
                            span
                            fw={600}
                        >
                            Infrastructure repository:
                        </Text>{' '}
                        {record.infraRepositoryName ?? 'not recorded'}
                    </Text>
                    <Text size={'sm'}>
                        <Text
                            span
                            fw={600}
                        >
                            Source pipeline:
                        </Text>{' '}
                        {record.sourcePipelineId ?? 'not recorded'}
                    </Text>
                    <Text size={'sm'}>
                        <Text
                            span
                            fw={600}
                        >
                            Infrastructure pipeline:
                        </Text>{' '}
                        {record.infraPipelineId ?? 'not recorded'}
                    </Text>
                    <Text size={'sm'}>
                        <Text
                            span
                            fw={600}
                        >
                            Deployment account group:
                        </Text>{' '}
                        {record.deploymentAccountGroupId ?? 'not recorded'}
                    </Text>
                </Stack>
            </Card>

            <Stack gap={'xs'}>
                <Title order={5}>Stages</Title>
                {stages.length === 0 ? (
                    <Text
                        size={'sm'}
                        c={'dimmed'}
                    >
                        No stages recorded. Nothing is recorded as provisioned for this
                        application.
                    </Text>
                ) : (
                    <Table
                        withTableBorder
                        verticalSpacing={'xs'}
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Stage</Table.Th>
                                <Table.Th>Account</Table.Th>
                                <Table.Th>Region</Table.Th>
                                <Table.Th>Stack</Table.Th>
                                <Table.Th>Domain</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {stages.map((stage) => (
                                <Table.Tr key={stage.stage}>
                                    <Table.Td>{stage.stage}</Table.Td>
                                    <Table.Td>{stage.accountId}</Table.Td>
                                    <Table.Td>{stage.region}</Table.Td>
                                    <Table.Td>{stage.infraStackName}</Table.Td>
                                    <Table.Td>{stage.domain ?? '—'}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
                <Text
                    size={'xs'}
                    c={'dimmed'}
                >
                    A stage that is not listed is recorded as not provisioned.
                </Text>
            </Stack>
        </Stack>
    );
};
